/**
 * useEscapeToClose — Escape dismisses the TOPMOST open hand-drawn modal
 * (ModalCard / ConfirmDialog). Neither modal had any keyboard escape before,
 * so a modal whose visible controls were unreachable could wedge the whole
 * app behind its full-screen overlay (MD-1030 E2E "ghost overlay").
 *
 * A module-level stack keeps nested modals honest: Escape only closes the one
 * on top. A `busy` modal ignores Escape (a write is in flight), and Escape is
 * also ignored while a Radix popper layer (combobox/popover/dropdown) is open
 * — Radix closes that layer itself, and one keypress must not close both.
 * For Media Devoted | Created By James Alexandrov
 */

import { useEffect, useRef } from "react";

const stack: symbol[] = [];

export function useEscapeToClose(
  open: boolean,
  busy: boolean,
  onClose: () => void,
): void {
  // Refs so the subscription survives re-renders without re-ordering the stack.
  const close = useRef(onClose);
  close.current = onClose;
  const isBusy = useRef(busy);
  isBusy.current = busy;
  const id = useRef<symbol | null>(null);
  if (id.current === null) id.current = Symbol("modal");

  useEffect(() => {
    if (!open) return;
    const me = id.current as symbol;
    stack.push(me);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (stack[stack.length - 1] !== me || isBusy.current) return;
      // An open Radix layer (e.g. a SearchableSelect inside the modal) owns
      // this Escape — it closes itself.
      if (document.querySelector("[data-radix-popper-content-wrapper]")) return;
      close.current();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      const i = stack.indexOf(me);
      if (i >= 0) stack.splice(i, 1);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);
}

export default useEscapeToClose;
